import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { useOnboarding } from "@/context/OnboardingContext";
import { useStepValidation } from "@/hooks/useStepValidation";
import { OnboardingErrorModal } from "./OnboardingErrorModal";
import { debugLogger } from '@/utils/debugLogger';
import StickyOnboardingFooter from "./StickyOnboardingFooter";

const RoleStep = () => {
  const { data, updateData, nextStep } = useOnboarding();
  const { validateAndSaveStep, isValidating } = useStepValidation();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    message: string;
    timestamp: string;
    step: number;
    type: string;
  } | null>(null);
  const selectedRole = data.role;

  const handleSelectRole = (role: string) => {
    debugLogger.debug('Role selected:', role);
    updateData({ role: role as any });
  };

  const handleNext = async () => {
    debugLogger.debug('Attempting to save role step with data:', { role: data.role });
    
    const success = await validateAndSaveStep(
      data,
      {
        stepNumber: 4,
        customValidation: (stepData) => {
          debugLogger.debug('Validating role step data:', stepData);
          if (!stepData.role || stepData.role.trim() === '') {
            return { isValid: false, error: 'Selecione seu papel para continuar' };
          }
          // Validar se o role é um dos valores válidos
          if (!['sponsor', 'sponsored'].includes(stepData.role)) {
            return { isValid: false, error: 'Papel selecionado inválido' };
          }
          return { isValid: true };
        },
        onSuccess: () => {
          debugLogger.debug('Role step completed successfully with role:', data.role);
          nextStep();
        },
        onError: (error, details) => {
          debugLogger.error('Role step failed:', error);
          
          const safeErrorDetails = {
            message: `Erro crítico ao salvar papel selecionado: ${error}`,
            timestamp: details?.timestamp || new Date().toISOString(),
            step: details?.step || 4,
            type: details?.type || 'role_save_failed',
            selectedRole: data.role
          };
          
          setErrorDetails(safeErrorDetails);
          setShowErrorModal(true);
        }
      }
    );

    if (!success) {
      debugLogger.error('Role step validation failed for role:', data.role);
    }
  };

  const handleRetry = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
    debugLogger.debug('Retrying role step save with role:', data.role);
    handleNext();
  };

  return (
    <>
      <StickyOnboardingFooter
        buttonText="Continuar"
        onClick={handleNext}
        disabled={!selectedRole}
        isLoading={isValidating}
        loadingText="Salvando papel..."
      >
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-4 md:mb-8">
            <h1 className="text-xl md:text-2xl font-bold text-white mb-1 md:mb-2">
              Qual é o seu papel?
            </h1>
            <p className="text-xs md:text-sm text-white/70">
              Escolha como você deseja participar da plataforma
            </p>
          </div>

          <div className="space-y-2 md:space-y-4">
            <Card 
              className={`bg-black/40 backdrop-blur-sm border-2 shadow-2xl cursor-pointer transition-all duration-300 hover:bg-black/50 ${
                selectedRole === "sponsored" ? "border-luxury-pinkBright shadow-luxury-pinkBright/25" : "border-transparent"
              }`}
              onClick={() => handleSelectRole("sponsored")}
              data-testid="role-card-sponsored"
            >
              <div className="p-4 md:p-6 flex items-center justify-between">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="p-2 md:p-3 rounded-full bg-pink-500/20">
                    <span className="text-xl md:text-2xl">💎</span>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-medium text-white">Patrocinada</h3>
                    <p className="text-white/70 text-xs md:text-sm">Busco apoio financeiro</p>
                  </div>
                </div>
                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                  selectedRole === "sponsored" ? "bg-luxury-pinkBright border-luxury-pinkBright" : "border-white/30"
                }`}>
                  {selectedRole === "sponsored" && (
                    <div className="h-2 w-2 rounded-full bg-white"></div>
                  )}
                </div>
              </div>
            </Card>

            <Card 
              className={`bg-black/40 backdrop-blur-sm border-2 shadow-2xl cursor-pointer transition-all duration-300 hover:bg-black/50 ${
                selectedRole === "sponsor" ? "border-luxury-pinkBright shadow-luxury-pinkBright/25" : "border-transparent"
              }`}
              onClick={() => handleSelectRole("sponsor")}
              data-testid="role-card-sponsor"
            >
              <div className="p-4 md:p-6 flex items-center justify-between">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="p-2 md:p-3 rounded-full bg-blue-500/20">
                    <span className="text-xl md:text-2xl">👔</span>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-medium text-white">Patrocinador</h3>
                    <p className="text-white/70 text-xs md:text-sm">Ofereço apoio financeiro</p>
                  </div>
                </div>
                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                  selectedRole === "sponsor" ? "bg-luxury-pinkBright border-luxury-pinkBright" : "border-white/30"
                }`}>
                  {selectedRole === "sponsor" && (
                    <div className="h-2 w-2 rounded-full bg-white"></div>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {selectedRole && (
            <div className="mt-4 md:mt-6 p-3 md:p-4 bg-white/5 border border-white/10 rounded-lg" data-testid="role-info-box">
              <p className="text-white/80 text-sm">
                <strong>Papel selecionado:</strong> {selectedRole === "sponsored" ? "Patrocinada" : "Patrocinador"}
              </p>
              <p className="text-white/60 text-xs mt-1">
                {selectedRole === "sponsored" 
                  ? "Você receberá apoio financeiro de patrocinadores e participará de experiências exclusivas." 
                  : "Você oferecerá apoio financeiro a patrocinadas e terá acesso a benefícios premium."
                }
              </p>
            </div>
          )}
        </div>
      </StickyOnboardingFooter>

      {showErrorModal && errorDetails && (
        <OnboardingErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          onRetry={handleRetry}
          stepNumber={4}
          errorDetails={errorDetails}
        />
      )}
    </>
  );
};

export default RoleStep;
